import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText';
import { IncludedTranscripts } from '@components/interview/analysis-components/IncludeTranscripts';
import { Metadata } from '@components/interview/analysis-components/MetaData';
import { Summary } from '@components/interview/analysis-components/Summary';
import { getQuoteText } from '@utils/representativeQuotes';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew'; // Perspectives
import BarChartIcon from '@mui/icons-material/BarChart'; // Signal Distribution
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupWorkIcon from '@mui/icons-material/GroupWork'; // Concept
import ScienceIcon from '@mui/icons-material/Science'; // Saturation Status
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt'; // Recommendations
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { AnalysisLayout } from './layout';

// Helper to safely parse JSON results
const safeParseResults = (results) => {
  if (!results) return { data: null, error: 'Results data is missing.' };
  try {
    const parsedData = typeof results === 'string' ? JSON.parse(results) : results;
    return { data: parsedData, error: null };
  } catch (error) {
    console.error('Error parsing SaturationGaugeAnalysis results:', error);
    return { data: null, error: `Failed to parse results: ${error.message}` };
  }
};

// Helper to get saturation status style
const getSaturationStatusStyle = (status, theme) => {
  switch (status) {
    case 'high':
      return { label: 'High', color: theme.palette.success.main };
    case 'moderate':
      return { label: 'Moderate', color: theme.palette.warning.main };
    case 'low':
      return { label: 'Low', color: theme.palette.error.main };
    default:
      return { label: status || 'Unknown', color: theme.palette.grey[500] };
  }
};

const SaturationGaugeAnalysis = ({ item }) => {
  const theme = useTheme();

  if (!item) {
    return <Alert severity="warning">Analysis item data is missing.</Alert>;
  }

  const { analysis_lens_type, results: rawResults } = item;
  const { data: results, error: parseError } = safeParseResults(rawResults);

  if (parseError) {
    return <Alert severity="error">{parseError}</Alert>;
  }

  if (!results) {
    return <Alert severity="warning">Parsed analysis results are empty.</Alert>;
  }

  // Destructure safely with defaults
  const {
    transcripts_analyzed = 0,
    total_unique_concepts = 0,
    saturation_status_overall = 'unknown',
    concept_prevalence = [],
    signal_distribution = {
      repetition: 0,
      contradiction: 0,
      novelty: 0,
      silence: 0,
      convergence: 0,
    },
    underrepresented_perspectives = [],
    priority_recommendations = [],
  } = results;

  const saturationStyle = getSaturationStatusStyle(saturation_status_overall, theme);

  const renderQuotes = (quotes) => {
    if (!quotes || quotes.length === 0) return null;
    return (
      <List dense disablePadding sx={{ pl: 2, mt: 1, borderLeft: '2px solid #eee' }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
          Representative Quotes:
        </Typography>
        {quotes.map((quote, index) => (
          <ListItem key={index} disableGutters sx={{ pt: 0, pb: 0.5 }}>
            <ListItemText
              primaryTypographyProps={{
                variant: 'caption',
                fontStyle: 'italic',
                color: 'text.secondary',
              }}
            >
              {getQuoteText(quote)}
            </ListItemText>
          </ListItem>
        ))}
      </List>
    );
  };

  // Find max prevalence for scaling progress bars
  const maxPrevalence = Math.max(...concept_prevalence.map((c) => c.prevalence), 1); // Avoid division by zero

  return (
    <AnalysisLayout results={results} item={{ ...item, name: 'Saturation Gauge Analysis' }}>
      <CardHeader
        title={
          <Typography
            variant="h5"
            gutterBottom
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <ScienceIcon sx={{ verticalAlign: 'bottom', mr: 1 }} />
            Saturation Gauge Analysis
            {analysis_lens_type && (
              <AnalysisLensHelperText lensName={analysis_lens_type} level="run" size="small" />
            )}
          </Typography>
        }
      />
      <Divider />
      <CardContent>
        <Metadata item={item} />
        <Summary item={item} results={results} />
        {/* Counts & Status Section */}
        <Grid container spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
          <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
            <Typography variant="h4">{transcripts_analyzed}</Typography>
            <Typography variant="caption">Transcripts Analyzed</Typography>
          </Grid>
          <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
            <Typography variant="h4">{total_unique_concepts}</Typography>
            <Typography variant="caption">Unique Concepts Identified</Typography>
          </Grid>
          <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
            <Chip
              icon={<ScienceIcon />}
              label={`Saturation: ${saturationStyle.label}`}
              sx={{
                backgroundColor: alpha(saturationStyle.color, 0.1),
                color: saturationStyle.color,
                fontWeight: 'medium',
                fontSize: '1rem', // Larger chip
                p: 1,
              }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Signal Distribution Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            <BarChartIcon sx={{ verticalAlign: 'bottom', mr: 1, color: 'primary.main' }} />
            Signal Distribution
          </Typography>
          <Grid container spacing={2} sx={{ textAlign: 'center', mt: 1 }}>
            {Object.entries(signal_distribution).map(([key, value]) => (
              <Grid item xs={6} sm key={key}>
                <Typography variant="h5">{value}</Typography>
                <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                  {key}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Concept Prevalence Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Concept Prevalence ({concept_prevalence.length})
          </Typography>
          {concept_prevalence.length > 0 ? (
            concept_prevalence.map((concept, index) => (
              <Accordion key={index} TransitionProps={{ unmountOnExit: true }} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5 }}>
                      <GroupWorkIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography sx={{ flexGrow: 1, mr: 1 }}>
                      {concept.concept || 'Unnamed Concept'}
                    </Typography>
                    <Chip label={`Prevalence: ${concept.prevalence || 0}`} size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {/* Optional: Add a progress bar for visual prevalence */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" sx={{ mr: 1, minWidth: '50px' }}>
                      Impact:
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(concept.prevalence / maxPrevalence) * 100}
                      sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  {renderQuotes(concept.representative_quotes)}
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No concept prevalence data found.
            </Typography>
          )}
        </Box>

        {/* Underrepresented Perspectives Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            <AccessibilityNewIcon
              sx={{ verticalAlign: 'bottom', mr: 1, color: 'secondary.main' }}
            />
            Underrepresented Perspectives
          </Typography>
          {underrepresented_perspectives.length > 0 ? (
            <List dense>
              {underrepresented_perspectives.map((perspective, index) => (
                <ListItem key={index}>
                  <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5 }}>
                    <AccessibilityNewIcon fontSize="small" color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={perspective.startsWith('- ') ? perspective.substring(2) : perspective}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No specific underrepresented perspectives identified.
            </Typography>
          )}
        </Box>

        {/* Priority Recommendations Section */}
        <Box>
          <Typography variant="h6" gutterBottom>
            <ThumbUpAltIcon sx={{ verticalAlign: 'bottom', mr: 1, color: 'success.main' }} />
            Priority Recommendations
          </Typography>
          {priority_recommendations.length > 0 ? (
            <List dense>
              {priority_recommendations.map((rec, index) => (
                <ListItem key={index}>
                  <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5 }}>
                    <ThumbUpAltIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary={rec.startsWith('- ') ? rec.substring(2) : rec} />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No priority recommendations identified.
            </Typography>
          )}
        </Box>
        <IncludedTranscripts item={item} results={results} />
      </CardContent>
    </AnalysisLayout>
  );
};

SaturationGaugeAnalysis.propTypes = {
  /** The analysis item object containing results and metadata */
  item: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    study_id: PropTypes.string,
    created_at: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    analysis_lens_type: PropTypes.string,
    results: PropTypes.oneOfType([PropTypes.string, PropTypes.object]), // Can be stringified JSON or parsed object
  }),
};

export default SaturationGaugeAnalysis;
